import { Link } from "react-router-dom";
import {
  FlaskConical, Gauge, Fingerprint, ScrollText, CheckCircle2, ArrowRight,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { usePageMeta } from "../lib/seo";

const sections = [
  {
    id: "drug-testing",
    icon: FlaskConical,
    num: "01",
    title: "Drug Testing",
    items: [
      ["Pre-Employment Drug Testing", "Screen candidates before they start. Urine and oral fluid collections for DOT-regulated and non-DOT employers."],
      ["DOT and Federal Drug Testing", "Full compliance with 49 CFR Part 40 and SAMHSA Mandatory Guidelines. Certified collection for transportation, federal contractor, and safety-sensitive positions."],
      ["Random Pool Management", "We manage your random selection program so you stay compliant with DOT-mandated testing rates without the administrative burden."],
      ["Post-Accident Testing", "Available 24/7 for immediate response. Mobile collection at your worksite."],
      ["Reasonable Suspicion Testing", "When supervisors observe signs of impairment, we respond quickly and professionally."],
      ["Return-to-Duty Testing", "Coordinated testing for employees returning after a policy violation following SAP evaluation."],
    ],
  },
  {
    id: "alcohol-testing",
    icon: Gauge,
    num: "02",
    title: "Alcohol Testing",
    items: [
      ["DOT Alcohol Testing", "BAT-certified breath alcohol testing compliant with 49 CFR Part 40 for transportation and safety-sensitive industries."],
      ["Non-DOT Alcohol Testing", "For employers with internal alcohol testing policies who need certified, documented results."],
      ["Post-Accident and Random Alcohol Testing", "Available on short notice with mobile capability."],
    ],
  },
  {
    id: "fingerprinting",
    icon: Fingerprint,
    num: "03",
    title: "Fingerprinting",
    items: [
      ["Ink Card Fingerprinting", "Traditional ink and roll fingerprinting accepted by most state licensing boards and agencies."],
      ["Live Scan Fingerprinting", "Electronic fingerprint capture for faster processing and submission to federal and state agencies."],
    ],
    footer:
      "Who we serve: healthcare workers, educators, federal contractors, security personnel, real estate licensees, and any professional requiring fingerprinting for licensing or employment purposes.",
  },
  {
    id: "compliance-consulting",
    icon: ScrollText,
    num: "04",
    title: "Compliance Consulting",
    items: [
      ["DC and Maryland Employer Law Guidance", "We advise clients on DC's Cannabis Employment Protections Amendment Act, Maryland cannabis employment law, and how both intersect with federal testing requirements."],
      ["Written Policy Development", "A documented, legally reviewed drug-free workplace policy is your first line of defense. We help you build one that works."],
      ["Supervisor Reasonable Suspicion Training", "DOT requires supervisors of safety-sensitive employees to be trained to recognize signs of impairment. We deliver this training for your team."],
      ["Program Audits", "Already have a testing program? We review it for compliance gaps and provide a written remediation plan."],
    ],
  },
];

export default function Services() {
  usePageMeta(
    "Services | VeraScreen Solutions — Drug Testing, Alcohol Testing, Fingerprinting & Compliance",
    "Comprehensive screening services for DC and Maryland employers. DOT drug testing, BAT-certified alcohol testing, fingerprinting, and compliance consulting following federal DOT and SAMHSA guidelines."
  );

  return (
    <main data-testid="services-page">
      <section className="relative overflow-hidden bg-[#0E7B6B] py-24 md:py-28">
        <div className="grain-overlay" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <h1 className="font-display max-w-4xl text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.1}>Comprehensive Screening Services</MaskedLine>
            <MaskedLine delay={0.25}>for DC and Maryland Employers</MaskedLine>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 md:text-lg">
            Every service we deliver follows federal DOT and SAMHSA guidelines and DC and Maryland
            employer law — so your results are accurate, defensible, and delivered on time.
          </p>
        </div>
      </section>

      {sections.map((s, idx) => (
        <section
          key={s.id}
          id={s.id}
          data-testid={`services-section-${s.id}`}
          className={`py-20 md:py-24 ${idx % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]"}`}
        >
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <div className="flex items-start gap-5">
                <span className="font-display text-6xl font-extrabold leading-none text-[#0E7B6B]/15 md:text-7xl">
                  {s.num}
                </span>
                <div className="pt-1">
                  <s.icon size={30} className="text-[#0E7B6B]" strokeWidth={1.5} />
                  <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-[#1A3A6B] md:text-4xl">
                    {s.title}
                  </h2>
                </div>
              </div>
            </Reveal>

            <div className="mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2">
              {s.items.map(([title, body], i) => (
                <Reveal key={title} delay={i * 0.05}>
                  <div className="flex gap-4">
                    <CheckCircle2 size={20} className="mt-1 shrink-0 text-[#0E7B6B]" strokeWidth={1.5} />
                    <div>
                      <h3 className="font-display text-lg font-bold text-[#1A3A6B]">{title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-[#333333]/80">{body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            {s.footer && (
              <Reveal delay={0.15}>
                <p className="mt-12 border-l-2 border-[#B8962E] bg-[#B8962E]/5 p-5 text-sm leading-relaxed text-[#333333]/90">
                  {s.footer}
                </p>
              </Reveal>
            )}
          </div>
        </section>
      ))}

      <section className="relative overflow-hidden bg-[#1A3A6B] py-20">
        <div className="grain-overlay" />
        <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-white md:text-4xl">
              Not sure which service you need?
            </h2>
            <p className="mt-4 text-white/70">Tell us your situation — we'll point you in the right direction.</p>
            <Link to="/contact" data-testid="services-contact-btn" className="btn-gold mt-8">
              Contact Us <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
